import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/lib/ws";
import api from "@/lib/api";
import MapView, { driverIcon } from "@/components/MapView";
import { PanicTab } from "@/components/admin/PanicTab";
import { SubscriptionsTab } from "@/components/admin/SubscriptionsTab";
import { BottomNav } from "@/components/BottomNav";
import { Marker } from "react-leaflet";
import { Taxi, SignOut, Users, Car, CarProfile, ChartBar, Path, Plus, Trash, X, WarningOctagon, Crown } from "@phosphor-icons/react";

const DEFAULT_CENTER = [40.4168, -3.7038];

const USER_ROLE_PILL = (role) => {
  if (role === "admin") return "pill-error";
  if (role === "driver") return "pill-info";
  return "pill-online";
};
const RIDE_STATUS_PILL = (status) => {
  if (status === "completed") return "pill-online";
  if (status === "cancelled") return "pill-error";
  if (status === "in_progress") return "pill-info";
  return "pill-warn";
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [rides, setRides] = useState([]);
  const [panics, setPanics] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [s, d, u, r, p] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/drivers/online"),
      api.get("/admin/users"),
      api.get("/admin/rides"),
      api.get("/admin/panic"),
    ]);
    setStats(s.data); setDrivers(d.data); setUsers(u.data); setRides(r.data); setPanics(p.data);
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [refresh]);

  const handleWs = useCallback((msg) => {
    if (msg.type === "driver_location") {
      setDrivers((prev) => {
        const others = prev.filter(d => d.id !== msg.driver_id);
        return [...others, { id: msg.driver_id, name: msg.name, current_lat: msg.lat, current_lng: msg.lng, is_online: true }];
      });
    }
    if (msg.type === "ride_event") refresh();
    if (msg.type === "panic_alert") {
      setPanics((prev) => [msg.alert, ...prev.filter((a) => a.id !== msg.alert.id)]);
    }
  }, [refresh]);

  useWebSocket(handleWs, !!user);

  const resolvePanic = async (id) => {
    await api.post(`/admin/panic/${id}/resolve`);
    setPanics((prev) => prev.map((a) => (a.id === id ? { ...a, status: "resolved" } : a)));
  };
  const activePanics = panics.filter((a) => a.status === "active");

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError("");
    setCreateBusy(true);
    const fd = new FormData(e.target);
    try {
      await api.post("/admin/users", {
        email: fd.get("email"),
        password: fd.get("password"),
        name: fd.get("name"),
        phone: fd.get("phone") || null,
        role: fd.get("role"),
      });
      setShowCreate(false);
      e.target.reset();
      refresh();
    } catch (err) {
      setCreateError(err.response?.data?.detail || "Error al crear usuario");
    } finally {
      setCreateBusy(false);
    }
  };

  const handleDeleteUser = async (uid, name) => {
    if (!window.confirm(`¿Eliminar al usuario "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const Metric = ({ label, value, icon, color }) => (
    <div className="card" data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="label">{label}</span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: color + "20", color }}>{icon}</div>
      </div>
      <div className="metric">{value ?? 0}</div>
    </div>
  );

  const driverMarkers = drivers
    .filter(d => d.current_lat && d.current_lng)
    .map(d => ({ id: d.id, lat: d.current_lat, lng: d.current_lng, icon: driverIcon, popup: d.name }));

  const adminTabs = [
    { id: "dashboard", label: "Resumen", icon: ChartBar, testId: "admin-nav-dashboard" },
    { id: "drivers", label: "Conductores", icon: Car, testId: "admin-nav-drivers" },
    { id: "users", label: "Usuarios", icon: Users, testId: "admin-nav-users" },
    { id: "rides", label: "Viajes", icon: Path, testId: "admin-nav-rides" },
    { id: "subs", label: "Planes", icon: Crown, testId: "admin-nav-subs" },
    { id: "panic", label: "Pánico", icon: WarningOctagon, testId: "admin-nav-panic", badge: activePanics.length > 0 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-50" data-testid="admin-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <Taxi size={22} color="white" weight="duotone" />
          </div>
          <div>
            <div className="font-display font-black text-xl leading-none">TAXI AD2 · Admin</div>
            <div className="text-xs text-slate-500 mt-1">{user?.email}</div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="hidden md:flex gap-2">
          {["dashboard", "drivers", "users", "rides", "subs", "panic"].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg font-bold text-sm relative ${tab === t ? "bg-orange-50 text-orange-700" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`admin-tab-${t}`}>
              {t === "dashboard" && "Resumen"}
              {t === "drivers" && "Conductores"}
              {t === "users" && "Usuarios"}
              {t === "rides" && "Viajes"}
              {t === "subs" && "Suscripciones"}
              {t === "panic" && "Pánico"}
              {t === "panic" && activePanics.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="panic-badge">{activePanics.length}</span>
              )}
            </button>
          ))}
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout-btn">
            <SignOut size={16} className="inline sm:mr-1" /> <span className="hidden sm:inline">Salir</span>
          </button>
        </div>
      </header>

      <main className="p-4 md:p-6 max-w-7xl mx-auto pb-24 md:pb-6">
        {/* Active panic banner */}
        {activePanics.length > 0 && (
          <button onClick={() => setTab("panic")} className="w-full mb-6 bg-red-600 text-white rounded-2xl px-5 py-4 flex items-center gap-3 panic-pulse text-left" data-testid="admin-panic-banner">
            <WarningOctagon size={28} weight="fill" />
            <div className="flex-1">
              <div className="font-display font-bold text-lg">{activePanics.length} alerta(s) de pánico activa(s)</div>
              <div className="text-sm opacity-90">Haz clic para ver los detalles y atender la emergencia.</div>
            </div>
          </button>
        )}

        {tab === "dashboard" && (
          <div className="space-y-6 fade-in">
            <h1 className="font-display text-3xl font-black">Panel de control</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Metric label="Usuarios" value={stats.total_users} icon={<Users size={20} weight="duotone" />} color="#3B82F6" />
              <Metric label="Conductores" value={stats.total_drivers} icon={<Car size={20} weight="duotone" />} color="#10B981" />
              <Metric label="En línea" value={stats.online_drivers} icon={<CarProfile size={20} weight="duotone" />} color="#F5B700" />
              <Metric label="Viajes activos" value={stats.active_rides} icon={<Path size={20} weight="duotone" />} color="#8B5CF6" />
              <Metric label="Hoy" value={stats.rides_today} icon={<ChartBar size={20} weight="duotone" />} color="#F59E0B" />
              <Metric label="Completados" value={stats.completed_rides} icon={<Path size={20} weight="duotone" />} color="#10B981" />
              <Metric label="Pasajeros" value={stats.total_passengers} icon={<Users size={20} weight="duotone" />} color="#EC4899" />
            </div>

            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-display font-bold text-lg">Mapa en vivo · Conductores conectados</h2>
                <span className="pill pill-online"><span className="dot-pulse" /> {drivers.length}</span>
              </div>
              <div style={{ height: 460 }}>
                <MapView center={DEFAULT_CENTER} zoom={12} markers={driverMarkers}>
                  {driverMarkers.map(m => <Marker key={m.id} position={[m.lat, m.lng]} icon={driverIcon} />)}
                </MapView>
              </div>
            </div>
          </div>
        )}

        {tab === "drivers" && (
          <div className="fade-in">
            <h1 className="font-display text-3xl font-black mb-4">Conductores en línea</h1>
            <div className="card overflow-x-auto" style={{ padding: 0 }}>
              <table className="w-full min-w-[560px]" data-testid="admin-drivers-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  <tr><th className="px-4 py-3 text-left">Nombre</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Lat/Lng</th><th className="px-4 py-3 text-left">Estado</th></tr>
                </thead>
                <tbody>
                  {drivers.length === 0 && <tr><td colSpan="4" className="px-4 py-6 text-center text-slate-500">Sin conductores activos</td></tr>}
                  {drivers.map(d => (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-bold">{d.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{d.email}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono">{d.current_lat?.toFixed(4)}, {d.current_lng?.toFixed(4)}</td>
                      <td className="px-4 py-3"><span className="pill pill-online"><span className="dot-pulse" /> En línea</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="fade-in">
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-display text-3xl font-black">Todos los usuarios</h1>
              <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="admin-create-user-btn">
                <Plus size={16} className="inline mr-1" weight="bold" /> Crear usuario
              </button>
            </div>
            <div className="card overflow-x-auto" style={{ padding: 0 }}>
              <table className="w-full min-w-[720px]" data-testid="admin-users-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  <tr><th className="px-4 py-3 text-left">Nombre</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Rol</th><th className="px-4 py-3 text-left">Teléfono</th><th className="px-4 py-3 text-left">Registro</th><th className="px-4 py-3 text-right">Acciones</th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-bold">{u.name}</td>
                      <td className="px-4 py-3 text-sm">{u.email}</td>
                      <td className="px-4 py-3"><span className={`pill ${USER_ROLE_PILL(u.role)}`}>{u.role}</span></td>
                      <td className="px-4 py-3 text-sm">{u.phone || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        {u.id !== user?.id && (
                          <button onClick={() => handleDeleteUser(u.id, u.name)} className="text-red-600 hover:bg-red-50 rounded-lg p-2" data-testid={`delete-user-${u.id}`}>
                            <Trash size={16} weight="duotone" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "rides" && (
          <div className="fade-in">
            <h1 className="font-display text-3xl font-black mb-4">Viajes recientes</h1>
            <div className="card overflow-x-auto" style={{ padding: 0 }}>
              <table className="w-full min-w-[640px]" data-testid="admin-rides-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  <tr><th className="px-4 py-3 text-left">Cliente</th><th className="px-4 py-3 text-left">Conductor</th><th className="px-4 py-3 text-left">Ruta</th><th className="px-4 py-3 text-left">Estado</th><th className="px-4 py-3 text-left">Precio</th><th className="px-4 py-3 text-left">Fecha</th></tr>
                </thead>
                <tbody>
                  {rides.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-slate-500">Sin viajes</td></tr>}
                  {rides.map(r => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-sm font-bold">{r.passenger_name}</td>
                      <td className="px-4 py-3 text-sm">{r.driver_name || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[280px] truncate">{r.pickup_address} → {r.dest_address}</td>
                      <td className="px-4 py-3"><span className={`pill ${RIDE_STATUS_PILL(r.status)}`}>{r.status}</span></td>
                      <td className="px-4 py-3 font-bold">${(r.final_price || r.estimated_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "subs" && (
          <SubscriptionsTab users={users} />
        )}

        {tab === "panic" && (
          <PanicTab panics={panics} onResolve={resolvePanic} />
        )}

      </main>

      {/* Create user modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="create-user-modal">
          <form onSubmit={handleCreateUser} className="bg-white rounded-3xl p-8 w-full max-w-md fade-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-black">Nuevo usuario</h2>
              <button type="button" onClick={() => setShowCreate(false)} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div><label className="label">Nombre</label><input name="name" className="input-field" required data-testid="create-user-name" /></div>
              <div><label className="label">Email</label><input name="email" type="email" className="input-field" required data-testid="create-user-email" /></div>
              <div><label className="label">Teléfono</label><input name="phone" className="input-field" data-testid="create-user-phone" /></div>
              <div><label className="label">Contraseña (mín. 6)</label><input name="password" type="password" minLength={6} className="input-field" required data-testid="create-user-password" /></div>
              <div>
                <label className="label">Rol</label>
                <select name="role" className="input-field" required defaultValue="passenger" data-testid="create-user-role">
                  <option value="passenger">Cliente</option>
                  <option value="driver">Conductor</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            {createError && <div className="mt-3 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{createError}</div>}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost flex-1">Cancelar</button>
              <button type="submit" disabled={createBusy} className="btn-primary flex-1" data-testid="create-user-submit">
                {createBusy ? "Creando..." : "Crear"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mobile bottom navigation */}
      <div className="md:hidden">
        <BottomNav tabs={adminTabs} active={tab} onChange={setTab} />
      </div>
    </div>
  );
}
